import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Article Carousel module.
 * Shows one article at a time; prev/next arrows fade a new article in.
 */
export default class ArticleCarousel extends MessageBus {
  /**
   * Constructor function of the ArticleCarousel class. Must receive one argument,
   * a DOM node representing the article carousel block element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node passed into the constructor
     */
    this.element = element;

    /**
     * The index of the currently visible slide.
     * @property {number} currentIndex - Zero-based index of the active slide.
     */
    this.currentIndex = 0;

    /**
     * The class applied to the visible slide.
     * @property {string} activeClass - CSS class toggled to fade slides in/out.
     */
    this.activeClass = 'is-active';

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {ArticleCarousel} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {ArticleCarousel} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.slides = Array.from(
      this.element.querySelectorAll(SELECTORS.ARTICLE_CAROUSEL_SLIDE)
    );

    this.nav = this.element.querySelector(SELECTORS.ARTICLE_CAROUSEL_NAV);

    this.prevButton = this.element.querySelector(
      SELECTORS.ARTICLE_CAROUSEL_PREV
    );

    this.nextButton = this.element.querySelector(
      SELECTORS.ARTICLE_CAROUSEL_NEXT
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {ArticleCarousel} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with the view.
   * Hides the navigation entirely when there are not enough slides to cycle.
   * @returns {ArticleCarousel} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.slides.length) return this;

    // Nothing to cycle — hide the nav and bail
    if (this.slides.length < 2) {
      if (this.nav) this.nav.hidden = true;
      return this;
    }

    // Respect a server-rendered active slide if one exists
    const renderedIndex = this.slides.findIndex((slide) =>
      slide.classList.contains(this.activeClass)
    );
    this.currentIndex = renderedIndex >= 0 ? renderedIndex : 0;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }

    return this;
  }

  // ---------------------------------------------------------------------------
  // Slide switching
  // ---------------------------------------------------------------------------

  /**
   * Fades in the slide at the given index and hides the current one.
   * Indexes wrap around, so navigating past either end loops the carousel.
   * Keeps `aria-hidden` in sync so hidden slides are ignored by screen readers.
   * @param {number} index - The index of the slide to activate; may be out of bounds.
   * @returns {ArticleCarousel} A reference to the current instance of the class
   * @chainable
   */
  goTo(index) {
    const targetIndex = (index + this.slides.length) % this.slides.length;

    if (targetIndex === this.currentIndex) return this;

    const currentSlide = this.slides[this.currentIndex];
    const targetSlide = this.slides[targetIndex];

    currentSlide.classList.remove(this.activeClass);
    currentSlide.setAttribute('aria-hidden', 'true');

    targetSlide.classList.add(this.activeClass);
    targetSlide.removeAttribute('aria-hidden');

    this.currentIndex = targetIndex;

    return this;
  }

  // ---------------------------------------------------------------------------
  // Arrow clicks
  // ---------------------------------------------------------------------------

  /**
   * Handles a click on the previous arrow button.
   */
  handlePrevClick() {
    this.goTo(this.currentIndex - 1);
  }

  /**
   * Handles a click on the next arrow button.
   */
  handleNextClick() {
    this.goTo(this.currentIndex + 1);
  }
}
